import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import Starfield from "@/components/Starfield";
import GoogleButton from "@/components/GoogleButton";
import { getDetectedReferralCode, clearReferralAttribution } from "@/lib/attribution";

export default function UserSignup() {
  const { register } = useAuth();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", referral_code: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    const detected = getDetectedReferralCode();
    if (detected) setF((prev) => ({ ...prev, referral_code: detected }));
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    if (f.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      setLoading(true);
      await register({ ...f, referral_code: f.referral_code ? f.referral_code.trim().toUpperCase() : null });
      clearReferralAttribution();
      toast.success("Account created. Welcome.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Signup failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="relative min-h-screen grid place-items-center bg-void overflow-hidden py-16" data-testid="user-signup-page">
      <Starfield count={60} />
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.8, 0, 0.2, 1] }}
        onSubmit={onSubmit}
        className="relative z-10 w-[92vw] max-w-md rounded-3xl gradient-border bg-[#141323] p-8 md:p-10"
      >
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.svg" alt="CaarSaarthi" className="w-8 h-8 rounded-full" />
          <span className="font-serif text-2xl text-white tracking-tight">CaarSaarthi</span>
        </Link>
        <h1 className="mt-8 font-serif text-4xl md:text-5xl text-white leading-tight">
          Start your <span className="italic gradient-text">saving.</span>
        </h1>
        <p className="mt-3 text-sm text-silver/60">Create an account to track your buyer-side deal in real time.</p>

        <label className="block eyebrow gradient-text mt-8">Full Name</label>
        <Input value={f.name} onChange={on("name")} placeholder="Rohan Malhotra" className="mt-3 h-12 bg-void border-white/10 text-white" data-testid="signup-name-input" />
        <label className="block eyebrow gradient-text mt-5">Email</label>
        <Input value={f.email} onChange={on("email")} type="email" placeholder="you@example.com" className="mt-3 h-12 bg-void border-white/10 text-white" data-testid="signup-email-input" />
        <label className="block eyebrow gradient-text mt-5">Phone</label>
        <Input value={f.phone} onChange={on("phone")} placeholder="+91 98XXX XXXXX" className="mt-3 h-12 bg-void border-white/10 text-white" data-testid="signup-phone-input" />
        <label className="block eyebrow gradient-text mt-5">Password</label>
        <Input value={f.password} onChange={on("password")} type="password" placeholder="min 6 characters" className="mt-3 h-12 bg-void border-white/10 text-white" data-testid="signup-password-input" />

        <label className="block eyebrow gradient-text mt-5">Referral Code (optional)</label>
        <Input
          value={f.referral_code}
          onChange={(e) => setF({ ...f, referral_code: e.target.value.toUpperCase() })}
          placeholder="AKASH7F3A"
          className="mt-3 h-12 bg-void border-white/10 text-white uppercase tracking-widest"
          data-testid="signup-referral-input"
        />
        <p className="mt-2 text-xs text-silver/40">Have a friend's CaarSaarthi referral code? Enter it here.</p>
        {f.referral_code && (
          <div className="mt-3 rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/10 px-4 py-3 text-xs text-[#C4B5FD]" data-testid="referral-detected-banner">
            You were referred by a CaarSaarthi member. Code <span className="font-mono font-semibold">{f.referral_code}</span> will be linked to this new account.
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="mt-8 w-full py-3.5 rounded-full font-medium text-white gradient-bg disabled:opacity-60 hover:scale-[1.01] transition-transform"
          data-testid="signup-submit"
        >
          {loading ? "Creating…" : "Create account"}
        </button>
        <div className="mt-6 flex items-center gap-3 text-xs text-silver/40">
          <span className="flex-1 h-px bg-white/10" />
          <span>or</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <GoogleButton label="Sign up with Google" testid="google-signup-btn" />
        <div className="mt-6 flex items-center justify-between text-xs text-silver/60">
          <Link to="/" className="hover:text-silver">← Back to site</Link>
          <Link to="/login" className="hover:text-white" data-testid="link-to-login">Already have an account? Sign in →</Link>
        </div>
      </motion.form>
    </main>
  );
}
